import {
  AUDIENCE_FIELD_DEFINITIONS,
  BRAND_FIELD_DEFINITIONS,
  BRAND_LIMITS,
  type AudienceFieldKey,
  type BrandFieldKey,
  type BrandListFieldKey,
  type ValuePhilosophy,
} from '@mg/shared';
import { TAG_PLACEHOLDERS } from '../../brand/brandCopy';
import { BrandFieldShell } from '../../brand/fields/BrandFieldShell';
import { BrandTextField } from '../../brand/fields/BrandTextField';
import { GuidelinesTable } from '../../brand/fields/GuidelinesTable';
import { TagListInput, type TagTone } from '../../brand/fields/TagListInput';
import { ValuePhilosophyField } from '../../brand/fields/ValuePhilosophyField';
import { brandFieldDescribedBy, fieldPartIds } from '../../brand/fields/fieldParts';
import { normalizeHashtag } from '../../brand/tagRules';
import { useBrandDraft } from '../draft/BrandDraftContext';
import { audienceFieldId, wizardFieldId } from '../draft/brandDraft';
import { useRevisitField } from '../revisit/useRevisitField';
import type { WizardFieldRef } from '../steps';
import { SuggestionSlot } from '../suggestions/SuggestionSlot';
import { useFieldSuggestion } from '../suggestions/useFieldSuggestion';
import { GUIDELINES_FIELD_LABEL } from '../wizardCopy';

type VocabularyKey = Exclude<BrandListFieldKey, 'writingGuidelines'>;

/** Each Vocabulary list's colour (spec:263): teal, Always say green with a check, Never say red with a slash. */
const TAG_TONES: Record<VocabularyKey, TagTone> = {
  keywords: 'teal',
  alwaysSay: 'always',
  neverSay: 'never',
  socialHashtags: 'teal',
  catchphrases: 'teal',
};

const textOf = (value: unknown): string | null => (typeof value === 'string' ? value : null);
const listOf = (value: unknown): string[] => (Array.isArray(value) ? value : []);

/**
 * One brand field of the wizard (web.md §4.2): the kit's control for its kind, with its source tags, its
 * hint under the label, and Release 4's hooks (Show changes in place of the control, the suggestion box
 * as its footer, the AI tag while a suggestion is offered). One per field, so each calls the hooks once.
 */
export function WizardField({ fieldKey, disabled = false }: { fieldKey: BrandFieldKey; disabled?: boolean }) {
  const { state, dispatch } = useBrandDraft();
  const ref: WizardFieldRef = { kind: 'field', key: fieldKey };
  const revisit = useRevisitField(ref);
  const suggestion = useFieldSuggestion(ref);

  const def = BRAND_FIELD_DEFINITIONS[fieldKey];
  const f = state.fields[fieldKey];
  const id = wizardFieldId(fieldKey);
  const aiSuggested = f.aiSuggested || suggestion.offered;
  const footer = <SuggestionSlot field={ref} />;
  const edit = (value: string | string[] | ValuePhilosophy | null) => dispatch({ type: 'edit', key: fieldKey, value });

  if (def.kind === 'text' || def.kind === 'textarea') {
    return (
      <BrandTextField
        id={id}
        def={def}
        value={textOf(f.value)}
        sources={f.sources}
        aiSuggested={aiSuggested}
        onChange={edit}
        readOnly={revisit.readOnly}
        disabled={disabled}
        changes={revisit.changes}
        footer={footer}
      />
    );
  }

  const describedBy = brandFieldDescribedBy(id, { hint: def.kind === 'guidelines' ? null : def.hint, sources: f.sources, aiSuggested });

  if (def.kind === 'tags') {
    const key = fieldKey as VocabularyKey;
    return (
      <BrandFieldShell
        id={id}
        label={def.label}
        hint={def.hint}
        sources={f.sources}
        aiSuggested={aiSuggested}
        readOnly={revisit.readOnly}
        changes={revisit.changes}
        footer={footer}
      >
        <TagListInput
          id={id}
          label={def.label}
          addPlaceholder={TAG_PLACEHOLDERS[key]}
          items={listOf(f.value)}
          onChange={edit}
          tone={TAG_TONES[key]}
          normalize={key === 'socialHashtags' ? normalizeHashtag : undefined}
          maxItems={def.maxItems ?? BRAND_LIMITS.tagsPerList}
          maxLength={def.maxLength}
          readOnly={revisit.readOnly}
          disabled={disabled}
          describedBy={describedBy}
        />
      </BrandFieldShell>
    );
  }

  if (def.kind === 'guidelines') {
    // The step card already says what the rules are for (the field's hint is the same sentence).
    return (
      <BrandFieldShell
        id={id}
        label={GUIDELINES_FIELD_LABEL}
        sources={f.sources}
        aiSuggested={aiSuggested}
        readOnly={revisit.readOnly}
        changes={revisit.changes}
        footer={footer}
      >
        <GuidelinesTable
          id={id}
          items={listOf(f.value)}
          onChange={edit}
          readOnly={revisit.readOnly}
          disabled={disabled}
          describedBy={describedBy}
        />
      </BrandFieldShell>
    );
  }

  // Value philosophy: a radio group, labelled by the shell's group label.
  return (
    <BrandFieldShell
      id={id}
      group
      label={def.label}
      hint={def.hint}
      sources={f.sources}
      aiSuggested={aiSuggested}
      readOnly={revisit.readOnly}
      changes={revisit.changes}
      footer={footer}
    >
      <ValuePhilosophyField
        id={id}
        labelId={fieldPartIds(id).label}
        value={textOf(f.value) as ValuePhilosophy | null}
        onChange={edit}
        readOnly={revisit.readOnly}
        disabled={disabled}
        describedBy={describedBy}
      />
    </BrandFieldShell>
  );
}

/** One field of an audience card (step 7), with its own source tags (spec:108). */
export function WizardAudienceField({
  cardKey,
  fieldKey,
  disabled = false,
}: {
  cardKey: string;
  fieldKey: AudienceFieldKey;
  disabled?: boolean;
}) {
  const { state, dispatch } = useBrandDraft();
  const ref: WizardFieldRef = { kind: 'audience', card: cardKey, key: fieldKey };
  const revisit = useRevisitField(ref);
  const suggestion = useFieldSuggestion(ref);
  const card = state.audiences.find((c) => c.key === cardKey);
  if (!card) return null;
  const f = card.fields[fieldKey];
  return (
    <BrandTextField
      id={audienceFieldId(cardKey, fieldKey)}
      def={AUDIENCE_FIELD_DEFINITIONS[fieldKey]}
      value={textOf(f.value)}
      sources={f.sources}
      aiSuggested={f.aiSuggested || suggestion.offered}
      onChange={(value) => dispatch({ type: 'editAudience', key: cardKey, field: fieldKey, value })}
      readOnly={revisit.readOnly}
      disabled={disabled}
      changes={revisit.changes}
      footer={<SuggestionSlot field={ref} />}
    />
  );
}
